// Client helpers that call the Graph Netlify functions (app-only, read-only).
// Used by EntraGroupBinder (group search) and the directory user picker.
import { supabase } from '@govern360/shared';

async function callFn(path, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
    body: JSON.stringify(body || {}),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Request failed (${r.status})`);
  return j;
}

// Live group search from the real directory. Returns null on failure so the
// binder can fall back to manual entry (graceful degradation preserved).
// MULTI-TENANT: routes to the Supabase edge function graph-group-search, which
// mints a token from THIS tenant's own entra_connections (secret-only, limit(1)).
// Previously called the Netlify function, which used a single global Graph app
// (GRAPH_* env vars) and only worked for one tenant.
export async function graphGroupSearch(q) {
  try {
    const { data, error } = await supabase.functions.invoke('graph-group-search', { body: { q } });
    if (error) throw new Error(error.message);
    return (data && data.groups) || [];
  } catch {
    return null;
  }
}

/* G360_K365 - NO CALLERS. This is the un-migrated path: the Netlify
   function uses one global Graph app rather than the calling tenant's
   own connection, and it returns 500 on Collette. Kept only so the
   decision to delete it, and the function behind it, is made
   deliberately rather than by accident. Do not wire anything to it. */
export async function graphListUsers(q) {
  const { users } = await callFn('/.netlify/functions/graph-list-users', { q });
  return users || [];
}

/* G360_K364 - was the Netlify function, which calls getGraphToken() with
   no tenant argument and uses the single global Graph app - the same fault
   graphGroupSearch above was migrated away from. Returns the full envelope
   now: truncated matters, because a partial member list presented as the
   group is the failure this replaces. No caller depended on the old array. */
export async function graphGroupMembers(groupId) {
  const { data, error } = await supabase.functions.invoke('graph-group-members', { body: { groupId } });
  if (error) throw new Error(error.message);
  const members = (data && data.members) || [];
  return { members, total: (data && data.total) || members.length, truncated: !!(data && data.truncated) };
}

// Per-tenant user search via the Supabase edge function (graph-users-search).
// Unlike graphListUsers (Netlify, global token), this uses each tenant's own
// Entra connection - the correct multi-tenant path.
//
// G360_K365 - this used to return an empty array when the call failed, so a
// broken integration and an empty directory were the same result. It throws
// now. TokenBudgetsSection already wraps it in try/catch and is unaffected;
// DirectoryUserPicker catches and offers manual entry, which is the honest
// answer to "we could not reach your directory".
export async function graphUsersSearchTenant(q) {
  try {
    const { data, error } = await supabase.functions.invoke('graph-users-search', { body: { q } });
    if (error) throw new Error(error.message);
    return (data && data.users) || [];
  } catch {
    throw new Error('Directory search is unavailable for this organisation.');
  }
}
